/**
 * localStorage utility for visitor management offline functionality
 * Provides local storage for visitor data when offline
 * Replaces IndexedDB implementation with localStorage for consistency
 */

class VisitorLocalStorage {
  constructor() {
    this.storageKeys = {
      visitors: 'visitor_data',
      visitor_logs: 'visitor_logs',
      visitor_photos: 'visitor_photos',
      visitor_documents: 'visitor_documents',
      pending_actions: 'pending_sync_visitors'
    };
  }

  /**
   * Check if localStorage is available
   */
  isSupported() {
    try {
      const test = 'test';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get data from localStorage with error handling
   */
  _getStorageData(key) {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error(`Error reading from localStorage key ${key}:`, error);
      return [];
    }
  }

  /**
   * Set data to localStorage with error handling
   */
  _setStorageData(key, data) {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error(`Error writing to localStorage key ${key}:`, error);
      return false;
    }
  }

  /**
   * Initialize the localStorage (no-op for localStorage)
   */
  async init() {
    if (!this.isSupported()) {
      throw new Error('localStorage is not supported in this browser');
    }
    console.log('localStorage initialized successfully');
    return Promise.resolve(true);
  }

  /**
   * Add or update a visitor
   */
  async saveVisitor(visitor) {
    const visitors = this._getStorageData(this.storageKeys.visitors);
    const visitorWithMeta = {
      ...visitor,
      last_synced: new Date().toISOString(),
      is_offline: true
    };

    const existingIndex = visitors.findIndex(v => v.id === visitor.id);
    if (existingIndex >= 0) {
      visitors[existingIndex] = visitorWithMeta;
    } else {
      visitors.push(visitorWithMeta);
    }

    this._setStorageData(this.storageKeys.visitors, visitors);
    return Promise.resolve(visitor.id);
  }

  /**
   * Get visitor by ID
   */
  async getVisitor(visitorId) {
    const visitors = this._getStorageData(this.storageKeys.visitors);
    const visitor = visitors.find(v => v.id === visitorId);
    return Promise.resolve(visitor || null);
  }

  /**
   * Get all visitors with optional filtering
   */
  async getVisitors(filters = {}) {
    let visitors = this._getStorageData(this.storageKeys.visitors);
    
    // Apply filters
    if (filters.status) {
      visitors = visitors.filter(v => v.status === filters.status);
    }
    if (filters.building_id) {
      visitors = visitors.filter(v => v.building_id === filters.building_id);
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      visitors = visitors.filter(v => 
        v.name?.toLowerCase().includes(searchLower) ||
        v.email?.toLowerCase().includes(searchLower) ||
        v.company?.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort by created_at descending
    visitors.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    return Promise.resolve(visitors);
  }

  /**
   * Get active visitors (checked in)
   */
  async getActiveVisitors(buildingId = null) {
    const filters = { status: 'checked_in' };
    if (buildingId) {
      filters.building_id = buildingId;
    }
    return this.getVisitors(filters);
  }

  /**
   * Delete visitor
   */
  async deleteVisitor(visitorId) {
    const visitors = this._getStorageData(this.storageKeys.visitors);
    const filteredVisitors = visitors.filter(v => v.id !== visitorId);
    this._setStorageData(this.storageKeys.visitors, filteredVisitors);
    return Promise.resolve(true);
  }

  /**
   * Save visitor log
   */
  async saveVisitorLog(log) {
    const logs = this._getStorageData(this.storageKeys.visitor_logs);
    const logWithMeta = {
      ...log,
      id: log.id || `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      created_at: log.created_at || new Date().toISOString()
    };

    const existingIndex = logs.findIndex(l => l.id === logWithMeta.id);
    if (existingIndex >= 0) {
      logs[existingIndex] = logWithMeta;
    } else {
      logs.push(logWithMeta);
    }

    this._setStorageData(this.storageKeys.visitor_logs, logs);
    return Promise.resolve(logWithMeta.id);
  }

  /**
   * Get visitor logs
   */
  async getVisitorLogs(visitorId) {
    const logs = this._getStorageData(this.storageKeys.visitor_logs);
    const visitorLogs = logs.filter(l => l.visitor_id === visitorId);
    visitorLogs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return Promise.resolve(visitorLogs);
  }

  /**
   * Save visitor photo
   */
  async saveVisitorPhoto(photo) {
    const photos = this._getStorageData(this.storageKeys.visitor_photos);
    const photoWithMeta = {
      ...photo,
      id: photo.id || `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      created_at: photo.created_at || new Date().toISOString()
    };

    const existingIndex = photos.findIndex(p => p.id === photoWithMeta.id);
    if (existingIndex >= 0) {
      photos[existingIndex] = photoWithMeta;
    } else {
      photos.push(photoWithMeta);
    }

    this._setStorageData(this.storageKeys.visitor_photos, photos);
    return Promise.resolve(photoWithMeta.id);
  }

  /**
   * Get visitor photos
   */
  async getVisitorPhotos(visitorId) {
    const photos = this._getStorageData(this.storageKeys.visitor_photos);
    const visitorPhotos = photos.filter(p => p.visitor_id === visitorId);
    return Promise.resolve(visitorPhotos);
  }

  /**
   * Save visitor document
   */
  async saveVisitorDocument(document) {
    const documents = this._getStorageData(this.storageKeys.visitor_documents);
    const documentWithMeta = {
      ...document,
      id: document.id || `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      created_at: document.created_at || new Date().toISOString()
    };

    const existingIndex = documents.findIndex(d => d.id === documentWithMeta.id);
    if (existingIndex >= 0) {
      documents[existingIndex] = documentWithMeta;
    } else {
      documents.push(documentWithMeta);
    }

    this._setStorageData(this.storageKeys.visitor_documents, documents);
    return Promise.resolve(documentWithMeta.id);
  }

  /**
   * Get visitor documents
   */
  async getVisitorDocuments(visitorId) {
    const documents = this._getStorageData(this.storageKeys.visitor_documents);
    const visitorDocuments = documents.filter(d => d.visitor_id === visitorId);
    return Promise.resolve(visitorDocuments);
  }

  /**
   * Save pending action for when back online
   */
  async savePendingAction(action) {
    const actions = this._getStorageData(this.storageKeys.pending_actions);
    const actionWithMeta = {
      ...action,
      id: action.id || `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      created_at: action.created_at || new Date().toISOString()
    };

    const existingIndex = actions.findIndex(a => a.id === actionWithMeta.id);
    if (existingIndex >= 0) {
      actions[existingIndex] = actionWithMeta;
    } else {
      actions.push(actionWithMeta);
    }

    this._setStorageData(this.storageKeys.pending_actions, actions);
    return Promise.resolve(actionWithMeta.id);
  }

  /**
   * Get all pending actions
   */
  async getPendingActions() {
    const actions = this._getStorageData(this.storageKeys.pending_actions);
    actions.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    return Promise.resolve(actions);
  }

  /**
   * Delete pending action
   */
  async deletePendingAction(actionId) {
    const actions = this._getStorageData(this.storageKeys.pending_actions);
    const filteredActions = actions.filter(a => a.id !== actionId);
    this._setStorageData(this.storageKeys.pending_actions, filteredActions);
    return Promise.resolve(true);
  }

  /**
   * Clear all data (for testing or reset)
   */
  async clearAllData() {
    Object.values(this.storageKeys).forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.error(`Error clearing localStorage key ${key}:`, error);
      }
    });
    return Promise.resolve(true);
  }

  /**
   * Get database statistics
   */
  async getStats() {
    const stats = {};
    Object.entries(this.storageKeys).forEach(([storeName, key]) => {
      const data = this._getStorageData(key);
      stats[storeName] = data.length;
    });
    return Promise.resolve(stats);
  }
}

// Create singleton instance
const visitorDB = new VisitorLocalStorage();

export default visitorDB;

// Export class for testing
export { VisitorLocalStorage };

// Utility functions for common operations
export const visitorDBUtils = {
  /**
   * Check if browser supports localStorage
   */
  isSupported: () => {
    try {
      const test = 'test';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  },

  /**
   * Check if we're currently offline
   */
  isOffline: () => {
    return !navigator.onLine;
  },

  /**
   * Generate unique ID for offline records
   */
  generateOfflineId: (prefix = 'offline') => {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  },

  /**
   * Format visitor data for offline storage
   */
  formatVisitorForOffline: (visitor) => {
    return {
      ...visitor,
      is_offline: true,
      last_synced: new Date().toISOString(),
      offline_id: visitor.id || visitorDBUtils.generateOfflineId('visitor')
    };
  },

  /**
   * Create pending action for sync when online
   */
  createPendingAction: (actionType, data, visitorId = null) => {
    return {
      id: visitorDBUtils.generateOfflineId('action'),
      action_type: actionType,
      data: data,
      visitor_id: visitorId,
      created_at: new Date().toISOString(),
      retry_count: 0,
      max_retries: 3
    };
  }
};